import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { COUNTRIES } from "../data";
import "./Navbar.css";

export default function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const dropdownRef = useRef(null);
  const location = useLocation();
  const NAV_DESTINATIONS = Object.values(COUNTRIES);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
    setActiveDropdown(null);
    setMobileExpanded(null);
  }, [location.pathname]);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const isActive = (path) => location.pathname === path;
  const isDestinationActive = () => location.pathname.startsWith("/study-in");
  const isUniversityActive = () => location.pathname.startsWith("/university");


  return (
    <nav className={`navbar ${scrolled ? "scrolled" : ""}`} ref={dropdownRef}>
      <Link to="/" className="nav-logo" onClick={() => setIsMenuOpen(false)}>
        <img src="/assets/TRANSPARENT_LOGO.png" alt="ISTP Overseas Logo" />
      </Link>

      {/* ── Desktop Links ── */}
      <div className={`nav-links ${isMenuOpen ? "open" : ""}`}>
        <Link to="/" className={`nav-link ${isActive("/") ? "active" : ""}`}>Home</Link>
        <Link to="/about" className={`nav-link ${isActive("/about") ? "active" : ""}`}>About Us</Link>
        <Link to="/services" className={`nav-link ${isActive("/services") ? "active" : ""}`}>Services</Link>

        {/* ── Destinations Dropdown ── */}
        <div className={`nav-dropdown-wrap ${isDestinationActive() ? "active-parent" : ""}`}>
          <button
            className={`nav-link nav-dropdown-trigger ${isDestinationActive() ? "active" : ""} ${activeDropdown === "destinations" ? "open" : ""}`}
            onClick={() => setActiveDropdown(activeDropdown === "destinations" ? null : "destinations")}
            aria-expanded={activeDropdown === "destinations"}
          >
            Study Destinations
            <svg className="nav-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>

          {activeDropdown === "destinations" && (
            <div className="nav-dropdown nav-dropdown--destinations">
              <div className="nav-dropdown-header">
                <span>Study Destinations</span>
                <p>Explore our top study abroad countries</p>
              </div>
              <div className="nav-dropdown-grid">
                {NAV_DESTINATIONS.map((dest) => (
                  <Link
                    key={dest.slug}
                    to={`/study-in/${dest.slug}`}
                    className="nav-dropdown-item"
                    onClick={() => setActiveDropdown(null)}
                  >
                    <span className="nav-di-flag">{dest.flag}</span>
                    <div>
                      <span className="nav-di-name">{dest.name}</span>
                      <span className="nav-di-sub">Study in {dest.name} →</span>
                    </div>
                  </Link>
                ))}
              </div>
              <div className="nav-dropdown-footer">
                <Link to="/book-slot" className="nav-dd-cta" onClick={() => setActiveDropdown(null)}>
                  Book Free Counselling →
                </Link>
              </div>
            </div>
          )}
        </div>
        <Link
          to="/universities"
          className={`nav-link ${location.pathname.startsWith("/universities") ||
            location.pathname.startsWith("/university")
            ? "active"
            : ""
            }`}
        >
          Universities
        </Link>
        <Link to="/student-meets" className={`nav-link ${isActive("/student-meets") ? "active" : ""}`}>Student Meets</Link>
        <Link to="/contact" className={`nav-link ${isActive("/contact") ? "active" : ""}`}>Contact</Link>
        <Link to="/book-slot" className="nav-cta">Book Counselling</Link>
      </div>

      {/* ── Mobile Menu ── */}
      <div className={`nav-mobile-menu ${isMenuOpen ? "open" : ""}`}>
        <Link to="/" className={`nav-link ${isActive("/") ? "active" : ""}`} onClick={() => setIsMenuOpen(false)}>Home</Link>
        <Link to="/about" className={`nav-link ${isActive("/about") ? "active" : ""}`} onClick={() => setIsMenuOpen(false)}>About Us</Link>
        <Link to="/services" className={`nav-link ${isActive("/services") ? "active" : ""}`} onClick={() => setIsMenuOpen(false)}>Services</Link>

        {/* Mobile Destinations Accordion */}
        <div className="nav-mobile-accordion">
          <button
            className={`nav-link nav-mobile-accordion-trigger ${mobileExpanded === "destinations" ? "open" : ""}`}
            onClick={() => setMobileExpanded(mobileExpanded === "destinations" ? null : "destinations")}
          >
            Study Destinations
            <svg className="nav-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
          {mobileExpanded === "destinations" && (
            <div className="nav-mobile-accordion-body">
              {NAV_DESTINATIONS.map((dest) => (
                <Link key={dest.slug} to={`/study-in/${dest.slug}`} className="nav-mobile-sub-link" onClick={() => setIsMenuOpen(false)}>
                  {dest.flag} Study in {dest.name}
                </Link>
              ))}
            </div>
          )}
        </div>
        <Link
          to="/universities"
          className={`nav-link ${location.pathname.startsWith("/universities") ||
            location.pathname.startsWith("/university")
            ? "active"
            : ""
            }`}
        >
          Universities
        </Link>

        <Link to="/student-meets" className={`nav-link ${isActive("/student-meets") ? "active" : ""}`} onClick={() => setIsMenuOpen(false)}>Student Meets</Link>
        <Link to="/contact" className={`nav-link ${isActive("/contact") ? "active" : ""}`} onClick={() => setIsMenuOpen(false)}>Contact</Link>
        <Link to="/book-slot" className="nav-cta nav-cta--mobile" onClick={() => setIsMenuOpen(false)}>Book Counselling</Link>
      </div>

      <button
        className="hamburger"
        onClick={() => { setIsMenuOpen(!isMenuOpen); setActiveDropdown(null); }}
        aria-label="Toggle menu"
      >
        {isMenuOpen ? (
          <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        )}
      </button>
    </nav>
  );
}